import { ReactNode, useCallback } from 'react';
import { UnreachableCaseError } from 'ts-essentials';
import toastMessage from '../toastMessage';
import { KOBOL_ERROR_MESSAGES } from '../../components/Chart/constants/utils';
import { ChartViewType, IChartError } from '../../components/Chart/types';
import { AnalysisErrorStatus } from '../apollo/graphqlSchema';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const handleCopyToClipboard = async (textToCopy: string) => {
  await navigator.clipboard.writeText(textToCopy);
  toastMessage({
    message: 'Error message copied to clipboard',
    type: 'info',
  });
};

const handleOpenSupport = () => {
  // Open intercom for support help
  window.Intercom('show');
  // Record intercom data to mark the reason of the call
  window.dataLayer.push({
    action: 'intercom_select',
    category: 'need_help',
  });
};

const ClickableSpan = styled.span`
  color: ${theme.colors.mainBlue};
  cursor: pointer;
  text-decoration: underline;
`;

type IKobolError = Extract<IChartError, { type: ChartViewType.KobolError }>;

const useKobolErrorInfo = () => {
  const getKobolTitleAndDescription = useCallback(
    ({ errorStatus, analysisId, failureMsg }: IKobolError) => {
      let title: ReactNode;
      let description: ReactNode;

      switch (errorStatus) {
        case AnalysisErrorStatus.Timeout:
          title = KOBOL_ERROR_MESSAGES[errorStatus].title;
          description = KOBOL_ERROR_MESSAGES[errorStatus].description;
          break;

        case AnalysisErrorStatus.EntityUpgradeError:
        case AnalysisErrorStatus.ServiceError:
        case AnalysisErrorStatus.Unexpected:
        case AnalysisErrorStatus.Unknown:
        case AnalysisErrorStatus.SqlError:
          title = KOBOL_ERROR_MESSAGES[errorStatus].title;

          const clipboardText =
            `Analysis ID ${analysisId}, status: ${errorStatus}, ` +
            `failure message: ${failureMsg ?? 'No error message'}`;

          description = (
            <div>
              <ClickableSpan
                onClick={() => handleCopyToClipboard(clipboardText)}
              >
                Click here
              </ClickableSpan>{' '}
              to copy the error Kubit received and{' '}
              <ClickableSpan onClick={handleOpenSupport}>
                reach out
              </ClickableSpan>{' '}
              to your Kubit Support Team for help.
            </div>
          );
          break;

        default:
          throw new UnreachableCaseError(errorStatus);
      }

      return { title, description };
    },
    [],
  );

  return { getKobolTitleAndDescription };
};

export default useKobolErrorInfo;
